import { Level } from "@pages/Contents/Skills/Levels/types";
import { Divider, IconButton, TextField, useTheme } from "@mui/material";
import { Delete, DragHandle } from "@mui/icons-material";
import { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";
import React from "react";
import { useTranslation } from "react-i18next";
import { SelectCst } from "@components/InputCst";
import { Options } from "@components/InputCst/SelectCst/types";
import ButtonCst from "@components/ButtonCst";
import {
  ContentColumn,
  ContentContainer,
  DeleteIcon,
  DescriptionRow,
  DragContainer,
  DragIcon,
  DragLabel,
  NameRow,
  RootContainer,
} from "@pages/Contents/Skills/Levels/components/LevelRow/style";

interface LevelRowProps {
  levelIndex: number;
  totalLevels: number;
  level: Level;
  errors: string[];
  onboardingLevels: Options[];
  onChange: (updatedLevel: Level) => void;
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
  onDelete: () => void;
  enableEditing: boolean;
}

const LevelRow = ({
  levelIndex,
  totalLevels,
  level,
  errors,
  onboardingLevels,
  onChange,
  dragHandleProps,
  onDelete,
  enableEditing,
}: LevelRowProps) => {
  const theme = useTheme();
  const { t } = useTranslation(["skills-levels-page", "common"]);

  return (
    <RootContainer theme={theme}>
      {enableEditing && (
        <DragContainer theme={theme}>
          <DragIcon {...dragHandleProps}>
            <DragHandle
              sx={{
                rotate: "90deg",
              }}
            />
            <DragLabel theme={theme}>{t("dnd-handle")}</DragLabel>
          </DragIcon>
        </DragContainer>
      )}

      <ContentContainer theme={theme}>
        <ContentColumn theme={theme}>
          <NameRow theme={theme}>
            <TextField
              error={errors.includes(`identifier_${level.id}`)}
              disabled={!level.can_edit || !enableEditing}
              required
              value={level.identifier}
              placeholder={t("identifier-label") ?? ""}
              label={t("identifier-label")}
              sx={{
                width: "30%",
                [theme.breakpoints.down("lg")]: {
                  width: "100%",
                },
              }}
              onChange={(e) =>
                onChange({ ...level, identifier: e.target.value as string })
              }
            />

            <TextField
              error={errors.includes(`name_${level.id}`)}
              disabled={!enableEditing}
              value={level.name}
              required
              placeholder={t("name-label") ?? ""}
              label={t("name-label")}
              sx={{
                width: "100%",
              }}
              onChange={(e) => {
                onChange({ ...level, name: e.target.value as string });
              }}
            />

            <SelectCst
              id={"onboarding_level"}
              disabled={!enableEditing}
              required
              error={errors.includes(`onboarding_level_${level.id}`)}
              placeholder={t("onboarding-level-label") ?? ""}
              label={t("onboarding-level-label") ?? ""}
              size={"medium"}
              variant={"outlined"}
              value={level.onboarding_level}
              options={onboardingLevels}
              onChange={(value) =>
                onChange({ ...level, onboarding_level: value })
              }
              sx={{
                [theme.breakpoints.down("lg")]: {
                  width: "100%",
                },
              }}
            />
          </NameRow>
          <DescriptionRow>
            <TextField
              disabled={!enableEditing}
              value={level.description}
              placeholder={t("description-label") ?? ""}
              label={t("description-label")}
              sx={{
                flex: 1,
              }}
              onChange={(e) => {
                if (e.target.value.length <= 100)
                  onChange({ ...level, description: e.target.value as string });
              }}
            />
          </DescriptionRow>

          {enableEditing && (
            <ButtonCst
              id={"back-button"}
              variant={"contained"}
              size={"medium"}
              sx={{
                display: "none",
                [theme.breakpoints.down("sm")]: {
                  minWidth: "100%",
                  display: "block",
                },
              }}
              onClick={onDelete}
            >
              {t("delete-level-cta")}
            </ButtonCst>
          )}
          <Divider />
        </ContentColumn>
      </ContentContainer>
      {enableEditing && (
        <DeleteIcon theme={theme}>
          <IconButton
            disabled={totalLevels === 1}
            onClick={onDelete}
            edge={"end"}
          >
            <Delete
              sx={{
                fill: theme.palette.primary.main,
                opacity: totalLevels > 1 ? 1 : 0.3,
              }}
            />
          </IconButton>
        </DeleteIcon>
      )}
    </RootContainer>
  );
};

export default LevelRow;
